import React, { FC, useEffect, useMemo, useState } from 'react';
import { Typography, Box, Stack, Portal, TextField, MenuItem, Tooltip, LinearProgress, Button, Chip } from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { StyledLink } from '../Components';
import { formatDateOnly } from '../Formatters';
import { isDefaultFilterModel, isDefaultSortModel, resetFilterModel, selectRevisionFilter, setFilterModel, setSortModel } from './filterSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { GET_REVISIONS } from './revisions.graphql';
import {
  progressResponsibilityRoles,
  revisionRolePeopleNames,
  revisionRoleToDisplayText,
  revisionTypeShortTexts,
  revisionTypeToShortText,
  sortProgressResponsibility,
} from './RevisionModel';
import { RevisionRoles } from '../__generated__/graphql';
import { useAuth } from 'oidc-react';
import { StyledPaper } from '../theme';
import { getShortName } from '../utils/string';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import {
  DataGridPro,
  getGridDateOperators,
  getGridSingleSelectOperators,
  GridColDef,
  gridFilteredSortedRowEntriesSelector,
  GridFilterInputValueProps,
  GridColumnMenu,
  GridColumnMenuManageItem,
  GridColumnMenuProps,
  GridFilterOperator,
  GridRenderCellParams,
  GridToolbarQuickFilter,
  GridValueGetter,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { newestAssessment, Revision } from './types';
import { ExcelExport } from './ExcelExport';
import { RevisionStatistics } from './RevisionStatistics';
import { isOngoing, overdueOperator, staleAssessmentOperator, staleOperator } from './statistics';

export const Overview: FC = () => {
  const { data, error } = useQuery(GET_REVISIONS, { fetchPolicy: 'cache-and-network' });
  const revisions = useMemo(() => data?.revisions?.filter((x) => !!x) ?? [], [data?.revisions]);
  return (
    <StyledPaper>
      <Typography variant="h5" component="h3">
        Revisjoner ({revisions.filter(isOngoing).length})
      </Typography>
      {error && <p>Error: {error.message}</p>}
      <SearchableRevisionTable revisions={revisions} loading={!error && !data?.revisions} />
    </StyledPaper>
  );
};

const SearchableRevisionTable: FC<{ revisions: Revision[]; loading: boolean }> = ({ revisions, loading }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const apiRef = useGridApiRef();
  const auth = useAuth();

  const searchTerm = searchParams.get('searchTerm');
  const { filterModel, sortModel } = useSelector(selectRevisionFilter);

  useEffect(() => {
    if (searchTerm) {
      dispatch(
        setFilterModel({
          items: [],
          quickFilterValues: [searchTerm],
        }),
      );
    }
  }, [searchTerm, apiRef]);

  const subjectId = auth.userData?.profile.sub;
  const categories = useMemo(() => toDistictCategories(revisions), [revisions]);
  const phases = useMemo(() => toDistinctPhases(revisions), [revisions]);
  const myCategories = useMemo<any[]>(
    () => toDistictCategories(revisions.filter((x) => x!.guide?.mainCategory?.ownerId === subjectId)),
    [revisions, subjectId],
  );

  const operator: GridFilterOperator<any, string> = useMemo(
    () => ({
      label: 'er mine',
      value: 'mine',
      getApplyFilterFn: (_filterItem, _column) => {
        return (value, _row, _column, _apiRef) => {
          return myCategories?.includes(value) ?? false;
        };
      },
    }),
    [myCategories],
  );

  const columns: GridColDef<Revision>[] = useMemo(
    () => [
      {
        field: 'guide.docName',
        headerName: 'Nummer',
        valueGetter: (_: GridValueGetter<Revision>, row) => row.guide?.docName,
        renderCell: (params: GridRenderCellParams<Revision, string>) => (
          <StyledLink to={`/revision/${params.row.id}`}>
            {params.row.guide?.docName ?? <span style={{ fontStyle: 'italic' }}>Uten navn</span>}
          </StyledLink>
        ),
      },
      {
        field: 'guide.docTitle',
        headerName: 'Tittel',
        flex: 2,
        valueGetter: (_: GridValueGetter<Revision>, row) => row.guide?.docTitle || 'Uten tittel',
      },
      {
        field: 'guide.category.title',
        width: 200,
        headerName: 'Fagområde',
        type: 'singleSelect',
        valueOptions: categories.map((title) => ({
          value: title!,
          label: title!,
        })),
        filterOperators: [...getGridSingleSelectOperators(), operator],
        valueGetter: (_: GridValueGetter<Revision>, row) => row.guide?.mainCategory?.title,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'singleSelect',
        valueOptions: revisionTypeShortTexts.filter((label) => revisions.some((r) => revisionTypeToShortText(r.type) === label)),
        width: 150,
        valueGetter: (_: GridValueGetter<Revision>, row) => revisionTypeToShortText(row.type),
      },
      {
        field: 'projectLeads',
        headerName: 'Prosjektleder',
        width: 150,
        valueGetter: (_: GridValueGetter<Revision>, row) => getProjectLeadNames(row),
      },
      {
        field: 'authors',
        headerName: 'Forfatter',
        width: 150,
        valueGetter: (_: GridValueGetter<Revision>, row) => getAuthorNames(row),
      },
      {
        field: 'currentPhase',
        headerName: 'Fase',
        type: 'singleSelect',
        valueOptions: phases,
        width: 180,
        valueGetter: (_: GridValueGetter<Revision>, row) => row.currentPhase,
        sortComparator: (a: string | null | undefined, b: string | null | undefined) => phaseNumber(a) - phaseNumber(b),
        renderCell: (params: GridRenderCellParams<Revision, string>) => <PhaseCell phase={params.value} inProgress={params.row.inProgressTasks} />,
      },
      {
        field: 'progress',
        headerName: 'Fremdrift',
        type: 'number',
        width: 110,
        valueGetter: (_: GridValueGetter<Revision>, row) => row.progress,
        valueFormatter: (value: number | null | undefined) => (value == null ? '' : `${value}%`),
        renderCell: (params: GridRenderCellParams<Revision, number>) => {
          if (params.value == null) return null;

          return (
            <Tooltip title={`${params.value}%`}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={params.value}
                  sx={{ width: '100%', height: 8, borderRadius: 4 }}
                  aria-label={`Fremdrift ${params.value} prosent`}
                />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'scheduledEndDate',
        headerName: 'Frist',
        type: 'date',
        width: 120,
        valueGetter: (_: GridValueGetter<Revision>, row) => (row.scheduledEndDate ? new Date(row.scheduledEndDate) : null),
        valueFormatter: (value) => formatDateOnly(value),
        filterOperators: [...getGridDateOperators(), overdueOperator],
      },
      {
        field: 'lastActivityAt',
        headerName: 'Sist aktivitet',
        type: 'date',
        width: 130,
        valueGetter: (_: GridValueGetter<Revision>, row) => (row.lastActivityAt ? new Date(row.lastActivityAt) : null),
        valueFormatter: (value) => formatDateOnly(value),
        filterOperators: [...getGridDateOperators(), staleOperator],
      },
      {
        field: 'projectLeadAssessments',
        headerName: 'Kommentar',
        type: 'date',
        width: 165,
        valueGetter: (_: GridValueGetter<Revision>, row) => {
          const newest = newestAssessment(row);
          return newest ? new Date(newest.createdAt) : null;
        },
        valueFormatter: (value) => formatDateOnly(value),
        renderCell: (params: GridRenderCellParams<Revision, Date | null>) => {
          const newest = newestAssessment(params.row);
          if (!newest) return null;

          return (
            <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{newest.text}</span>}>
              <span>{formatDateOnly(params.value)}</span>
            </Tooltip>
          );
        },
        filterOperators: [...getGridDateOperators(), staleAssessmentOperator],
      },
      {
        field: 'progressResponsibility',
        headerName: 'Ligger hos',
        width: 140,
        sortable: false,
        valueGetter: (_: GridValueGetter<Revision>, row) => sortProgressResponsibility(row.progressResponsibility),
        valueFormatter: (value: RevisionRoles[] | undefined) => (value ?? []).map(revisionRoleToDisplayText).join(', '),
        filterOperators: progressResponsibilityOperators,
        renderCell: (params: GridRenderCellParams<Revision, RevisionRoles[]>) => (
          <ProgressResponsibilityCell revision={params.row} roles={params.value ?? []} />
        ),
      },
      {
        field: 'publishedVersion',
        headerName: 'Versjon',
        width: 100,
        valueGetter: (_: GridValueGetter<Revision>, row) => row.publishedVersion,
      },
      {
        field: 'publishedAt',
        headerName: 'Publisert',
        type: 'date',
        width: 120,
        valueGetter: (_: GridValueGetter<Revision>, row) => (row.publishedAt ? new Date(row.publishedAt) : null),
        valueFormatter: (value) => formatDateOnly(value),
      },
      {
        field: 'completedAt',
        headerName: 'Ferdig',
        type: 'date',
        width: 120,
        valueGetter: (_: GridValueGetter<Revision>, row) => (row.completedAt ? new Date(row.completedAt) : null),
        valueFormatter: (value) => formatDateOnly(value),
      },
    ],
    [categories, phases, operator, revisions],
  );

  const inHistoryMode = searchTerm !== null;

  return (
    <Stack spacing={2} marginTop={2}>
      <ExcelExport apiRef={apiRef} />
      <Box id="filter-panel" minHeight={56} />
      <Box id="statistics-panel" />
      <DataGridPro
        autoHeight
        rows={revisions}
        loading={loading}
        apiRef={apiRef}
        filterModel={filterModel}
        sortModel={sortModel}
        disableRowSelectionOnClick
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        onFilterModelChange={(model) => dispatch(setFilterModel(model))}
        onSortModelChange={(model) => dispatch(setSortModel(model))}
        slots={{
          toolbar: CustomToolbar,
          columnMenu: ColumnMenu,
        }}
        slotProps={{ toolbar: { hasMyCategories: myCategories.length > 0 } as never }}
        density="compact"
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 50,
            },
          },
          columns: {
            columnVisibilityModel: {
              'guide.category.title': false,
              scheduledEndDate: false,
              publishedAt: inHistoryMode,
              publishedVersion: inHistoryMode,
            },
          },
        }}
      />
    </Stack>
  );
};

const ProgressResponsibilityFilterInput: FC<GridFilterInputValueProps> = ({ item, applyValue, focusElementRef }) => (
  <TextField
    select
    fullWidth
    variant="standard"
    label="Rolle"
    value={item.value ?? ''}
    inputRef={focusElementRef}
    onChange={(event) => applyValue({ ...item, value: event.target.value })}>
    {progressResponsibilityRoles.map((role) => (
      <MenuItem key={role} value={role}>
        {revisionRoleToDisplayText(role)}
      </MenuItem>
    ))}
  </TextField>
);

const progressResponsibilityOperators: GridFilterOperator<Revision, RevisionRoles[]>[] = [
  {
    label: 'ligger hos',
    value: 'includesRole',
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      return (value) => value?.includes(filterItem.value) ?? false;
    },
    InputComponent: ProgressResponsibilityFilterInput,
  },
  {
    label: 'ligger ikke hos',
    value: 'excludesRole',
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      return (value) => !(value?.includes(filterItem.value) ?? false);
    },
    InputComponent: ProgressResponsibilityFilterInput,
  },
  {
    label: 'er ikke satt',
    value: 'isEmpty',
    requiresFilterValue: false,
    getApplyFilterFn: () => (value) => !value || value.length === 0,
  },
];

function phaseNumber(phase: string | null | undefined) {
  const match = /^(\d+)/.exec(phase ?? '');
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

const PhaseCell: FC<{ phase: string | null | undefined; inProgress: readonly (string | null)[] | null | undefined }> = ({ phase, inProgress }) => {
  const started = (inProgress ?? []).filter((x): x is string => !!x);
  if (!phase) return null;

  return (
    <Tooltip
      title={
        started.length === 0 ? (
          'Ingen aktiviteter er påbegynt'
        ) : (
          <Box>
            <Box sx={{ fontWeight: 500 }}>Påbegynt:</Box>
            {started.map((name) => (
              <Box key={name}>{name}</Box>
            ))}
          </Box>
        )
      }>
      <Box sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{phase}</Box>
    </Tooltip>
  );
};

const ProgressResponsibilityCell: FC<{ revision: Revision; roles: RevisionRoles[] }> = ({ revision, roles }) => {
  if (roles.length === 0) return null;

  return (
    <Tooltip
      title={
        <Box>
          {roles.map((role) => (
            <Box key={role}>
              {revisionRoleToDisplayText(role)}: {revisionRolePeopleNames(revision, role).join(', ') || 'ingen'}
            </Box>
          ))}
        </Box>
      }>
      <Box sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {roles.map(revisionRoleToDisplayText).join(', ')}
      </Box>
    </Tooltip>
  );
};

function getProjectLeadNames(revision: Revision) {
  return getShortName(...(revision.projectLeads?.map((x) => x?.name) ?? []));
}

function getAuthorNames(revision: Revision) {
  return getShortName(...(revision.authors?.map((x) => x?.name) ?? []));
}

function CustomToolbar({ hasMyCategories }: { hasMyCategories?: boolean }) {
  const apiRef = useGridApiContext();
  const filteredRows = useGridSelector(apiRef, gridFilteredSortedRowEntriesSelector);

  return (
    <React.Fragment>
      <Portal container={() => document.getElementById('filter-panel')!}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <GridToolbarQuickFilter sx={{ flexGrow: 1 }} />
          <MyCategoriesFilter hasMyCategories={!!hasMyCategories} />
          <ResetFilter />
        </Stack>
      </Portal>
      <Portal container={() => document.getElementById('statistics-panel')!}>
        <RevisionStatistics revisions={filteredRows.map((entry) => entry.model as Revision)} />
      </Portal>
    </React.Fragment>
  );
}

function ColumnMenu(props: GridColumnMenuProps) {
  return <GridColumnMenu {...props} slots={{ columnMenuPinningItem: null, columnMenuColumnsItem: GridColumnMenuManageItem }} />;
}

const myCategoriesFilterId = 'quick:myCategories';

const MyCategoriesFilter: FC<{ hasMyCategories: boolean }> = ({ hasMyCategories }) => {
  const dispatch = useDispatch();
  const { filterModel } = useSelector(selectRevisionFilter);
  const active = filterModel.items.some((item) => item.id === myCategoriesFilterId);

  const toggle = () => {
    const remaining = filterModel.items.filter((item) => item.id !== myCategoriesFilterId);
    const item = { id: myCategoriesFilterId, field: 'guide.category.title', operator: 'mine' };
    dispatch(setFilterModel({ ...filterModel, items: active ? remaining : [...remaining, item] }));
  };

  const chip = (
    <Chip
      size="small"
      label="Mine fagområder"
      color={active ? 'primary' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      onClick={hasMyCategories ? toggle : undefined}
      disabled={!hasMyCategories}
      sx={{ flexShrink: 0 }}
    />
  );

  if (hasMyCategories) return chip;

  return (
    <Tooltip title="Du står ikke som fagområdeansvarlig for noen av anvisningene i listen.">
      <span>{chip}</span>
    </Tooltip>
  );
};

const ResetFilter: FC = () => {
  const dispatch = useDispatch();
  const { filterModel, sortModel } = useSelector(selectRevisionFilter);

  return (
    <Button
      size="small"
      color="inherit"
      startIcon={<FilterAltOffIcon />}
      disabled={isDefaultFilterModel(filterModel) && isDefaultSortModel(sortModel)}
      onClick={() => dispatch(resetFilterModel())}>
      Nullstill filter
    </Button>
  );
};

function toDistinctPhases(items: Revision[]): string[] {
  const set = new Set<string>();
  items.forEach((x) => {
    if (x.currentPhase) {
      set.add(x.currentPhase);
    }
  });
  return Array.from(set).sort((a, b) => phaseNumber(a) - phaseNumber(b));
}

function toDistictCategories(items: Revision[]): string[] {
  const set = new Set<string>();
  items.forEach((x) => {
    if (x.guide?.mainCategory?.title) {
      set.add(x.guide?.mainCategory.title);
    }
  });
  return Array.from(set).sort();
}
